/**
 * FloatingCreateButton Component
 * Circular FAB centered over the tab bar; navigates to create challenge.
 * Wrapper is full-width with pointerEvents='box-none' so tab bar taps still work.
 */

import React, { useCallback, useMemo } from 'react';
import { View, Text, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SoundPressable } from '../../src/shared/components/SoundPressable/SoundPressable';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';
import { useRouter, usePathname } from 'expo-router';
import { getFloatingCreateButtonStyles } from './FloatingCreateButton.styles';
import { useTheme } from '../../src/theme/useTheme';
import { useWalkthroughStep } from '../../src/shared/hooks/useWalkthroughStep';
import { useGuestWalkthroughStore } from '../../src/shared/stores/guestWalkthroughStore';
import { useFirstChallengeTarget } from '../../src/onboarding/hooks/useFirstChallengeTarget';
import { useFirstChallengeTourStore } from '../../src/onboarding/stores/firstChallengeTourStore';
import { FC_TARGET_KEYS } from '../../src/onboarding/constants/firstChallengeTour.constants';
import { WALKTHROUGH_STEPS } from '../../src/create/constants/guestWalkthrough.constants';
import { isWorldCupGroupWizardPath } from '../../src/worldcup/helpers/worldCupGroupWizardRoute.helper';
import { useWorldCupWelcomeMenuStore } from '../../src/worldcup/stores/worldCupWelcomeMenuStore';
import { useHasExistingChallenges } from '../../src/challenges/hooks/useHasExistingChallenges';
import { useSquadTvModeStore } from '../../src/worldcup/stores/squadTvModeStore';
import { useSplashStore } from '../../src/shared/stores/splashStore';
import { HIDE_FAB_ROUTES, FAB_BOTTOM_OFFSET } from './FloatingCreateButton.constants';

export const FloatingCreateButton: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const insets = useSafeAreaInsets();
  const worldCupWelcomeOverlayVisible = useWorldCupWelcomeMenuStore((s) => s.welcomeOverlayVisible);
  const isSquadTvModeActive = useSquadTvModeStore((s) => s.isActive);
  const theme = useTheme();
  const fabBottomOffset =
    FAB_BOTTOM_OFFSET + (Platform.OS === 'ios' ? insets.bottom : 0);
  const styles = useMemo(
    () => getFloatingCreateButtonStyles(theme, fabBottomOffset),
    [theme, fabBottomOffset],
  );

  const isWalkthroughActive = useGuestWalkthroughStore((s) => s.isActive);
  const currentStep = useGuestWalkthroughStore((s) => s.currentStep);
  const advanceStep = useGuestWalkthroughStore((s) => s.advanceStep);
  const { isResolved: challengesResolved, hasChallenges } = useHasExistingChallenges();
  // Native only: the animated "Stomp" splash overlays the app until it completes. The FAB has a high
  // zIndex/elevation and pokes through that overlay, so keep it hidden until the splash finishes.
  // (Web has no animated splash host, so animationComplete never flips there — guard by platform.)
  const splashAnimationComplete = useSplashStore((s) => s.animationComplete);
  const isSplashActive = Platform.OS !== 'web' && !splashAnimationComplete;

  const handlePress = useCallback(() => {
    if (isWalkthroughActive && currentStep === WALKTHROUGH_STEPS.CREATE_BTN) {
      advanceStep();
    }
    router.push('/app/create');
  }, [advanceStep, currentStep, isWalkthroughActive, router]);

  const { viewRef } = useWalkthroughStep(WALKTHROUGH_STEPS.CREATE_BTN, {
    onProxyTap: handlePress,
  });

  // First-challenge tour: spotlight the FAB on the "create" step (inert otherwise).
  const { viewRef: fcViewRef } = useFirstChallengeTarget(FC_TARGET_KEYS.create);
  const fcActive = useFirstChallengeTourStore((s) => s.active);
  const fcStep = useFirstChallengeTourStore((s) => s.step);
  const fcNeedsFab = fcActive && fcStep === 'create';
  const setButtonRef = useCallback(
    (node: React.ComponentRef<typeof SoundPressable> | null) => {
      (viewRef as React.MutableRefObject<unknown>).current = node;
      (fcViewRef as React.MutableRefObject<unknown>).current = node;
    },
    [viewRef, fcViewRef],
  );

  const shouldHide = useMemo(() => {
    if (isSplashActive) {
      return true;
    }
    if (isSquadTvModeActive) {
      return true;
    }
    if (worldCupWelcomeOverlayVisible) {
      return true;
    }
    if (pathname.includes('/league-detail')) {
      return true;
    }
    if (pathname.includes('/worldcup/match/')) {
      return true;
    }
    const segments = pathname.split('/').filter(Boolean);
    const routeSegments = segments.slice(1);
    const currentRoute = routeSegments.length === 0 ? 'index' : routeSegments[0];
    if (HIDE_FAB_ROUTES.includes(currentRoute)) {
      return true;
    }
    if (isWorldCupGroupWizardPath(pathname)) {
      return true;
    }
    const isArenaRoute = currentRoute === 'arena';
    const walkthroughNeedsFab =
      (isWalkthroughActive && currentStep === WALKTHROUGH_STEPS.CREATE_BTN) || fcNeedsFab;
    if (isArenaRoute && challengesResolved && !hasChallenges && !walkthroughNeedsFab) {
      return true;
    }
    return false;
  }, [
    pathname,
    worldCupWelcomeOverlayVisible,
    isSquadTvModeActive,
    isSplashActive,
    challengesResolved,
    hasChallenges,
    isWalkthroughActive,
    currentStep,
    fcNeedsFab,
  ]);

  if (shouldHide) {
    return null;
  }

  return (
    <View
      style={styles.wrapper}
      pointerEvents="box-none"
    >
      <View style={styles.halfCircle} pointerEvents="none" />
      <View style={styles.buttonContainer}>
      <SoundPressable
        ref={setButtonRef}
        style={({ pressed, hovered }) => [
          styles.button,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.buttonPressed,
          hovered && Platform.OS === 'web' && styles.containerHover,
        ]}
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel="Create challenge"
        accessibilityHint="Tap to create a new challenge"
        testID="floating-create-button"
      >
        <Text style={styles.plusIcon} accessible={false}>
          +
        </Text>
      </SoundPressable>
      </View>
    </View>
  );
};
